import { useEffect } from 'react';
import { useGetBatches } from 'shared/api/batches';
import type { Batch } from 'shared/api/batches/types';
import { downloadFile } from '../helpers';

export type UseBatches = {
  /**
   * The ID of the batch to be used for the operation.
   */
  batchId?: string;

  /**
   * Whether to execute the batch operation.
   */
  execute?: boolean;

  /**
   * The interval in milliseconds for refetching the batch status.
   */
  interval?: number;
};

/**
 * Custom hook to manage batch operations.
 *
 * @param params - Parameters for the batch operation, including batchId, execute flag, and refetch interval.
 */
export const useBatches = (params: UseBatches) => {
  const { batchId, execute = true, interval = 5000 } = params;

  /**
   * Hook to fetch batch data.
   */
  const { data, refetch } = useGetBatches({
    batchId,
    refetchInterval: newData => {
      if (
        execute &&
        ['waiting', 'processing'].includes(newData?.status || '')
      ) {
        return interval; // Refetch every specified interval if the batch is in progress
      }
      return false; // Stop refetching when the batch is finished or has errors
    },
  });

  /**
   * Effect to handle batch completion and download files.
   */
  useEffect(() => {
    if (execute && ['finished'].includes(data?.status || '')) {
      const response = data?.response as Batch['response'];

      if (
        response &&
        response.type === 'DOWNLOAD' &&
        response?.urls.length > 0
      ) {
        response.urls.forEach(({ url }) => {
          downloadFile(url);
        });
      }
    }
  }, [data, execute]);

  /**
   * Batch is error status
   */
  const isError = ['error', 'finished_with_errors'].includes(
    data?.status || '',
  );

  /**
   * Batch is loading status
   */
  const isLoading =
    !data || ['waiting', 'processing'].includes(data?.status || '');

  /**
   * Batch is finished
   */
  const isFinished = ['finished', 'finished_with_errors'].includes(
    data?.status || '',
  );

  /**
   * Batch is finished successfully
   */
  const isFinishedSuccessfully = data?.status === 'finished';

  /**
   * Batch is finished failure
   */
  const isFinishedFailure = data?.status === 'finished_with_errors';

  return {
    status: data?.status,
    data,
    refetch,
    isLoading,
    isError,
    isFinished,
    isFinishedSuccessfully,
    isFinishedFailure,
    errors: isError && data?.messages,
  };
};
